// Angular Import
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';

// bootstrap
import { NgbDropdown, NgbDropdownConfig } from '@ng-bootstrap/ng-bootstrap';
import { AuthService, HttpService } from 'common-libs';
import { NavigationExtras, Router } from '@angular/router';
import { GET_NOTIFICATION, GET_USER } from '../../../../../core/utility/apiRoutes';
import { environment } from '../../../../../../environments/environment';
import { interval, Subscription, switchMap } from 'rxjs';

@Component({
  selector: 'app-nav-right',
  templateUrl: './nav-right.component.html',
  styleUrls: ['./nav-right.component.scss'],
  providers: [NgbDropdownConfig],
  animations: [
    trigger('slideInOutLeft', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('300ms ease-in', style({ transform: 'translateX(0%)' })),
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ transform: 'translateX(100%)' })),
      ]),
    ]),
    trigger('slideInOutRight', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate('300ms ease-in', style({ transform: 'translateX(0%)' })),
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ transform: 'translateX(-100%)' })),
      ]),
    ]),
  ],
})
export class NavRightComponent implements OnInit, OnDestroy {
  // public props
  @ViewChild('notificationDropdown') dropdown!: NgbDropdown;
  visibleUserList: boolean;
  chatMessage: boolean;
  friendId!: number;
  userName: string = '';
  apiUrl: any = environment.apiUrl;
  id: string = '';
  profileImage: string = '';
  imageURL = environment.Image_url;
  defultImage = 'assets/images/user/user1.png';
  isadmin: boolean = false;
  branchName: string = '';
  userData: any = [];
  notificationData: any = [];
  audio = new Audio();
  previousNotificationIds: any[] = [];
  private isAuthenticated = false;

  // constructor
  constructor(
    private auth: AuthService,
    private route: Router,
    private _http: HttpService,
    private cdr: ChangeDetectorRef
  ) {
    this.visibleUserList = false;
    this.chatMessage = false;

    const token = this.auth.getAccessToken();
    this.isAuthenticated = !!token && !this.auth.isTokenExpired(token);

    if (!this.isAuthenticated) {
      // No logged-in user yet (e.g. this component is part of a shared
      // layout also mounted on the sign-in page) — skip everything below.
      return;
    }

    this.userName = this.auth.getUserInfo().Name;
    this.id = this.auth.getUserInfo().UserId;
    this.isadmin = this.auth.getRoleName().toLowerCase() == "admin";
    this.branchName = this.auth.getUserInfo().Branch
      ? this.auth.getUserInfo().Branch
      : this.auth.getUserInfo().Warehouse;

    this.getUserDetails();
    this.getAllNotification().subscribe();
  }

  notificationAppCall!: Subscription;
  userInteracted = false;

  ngOnInit(): void {
    if (!this.isAuthenticated) {
      return; // don't start polling if nobody's logged in
    }

    document.addEventListener('click', this.handleUserInteraction, { once: true });
    this.notificationAppCall = interval(5000) // Every 5 seconds
      .pipe(switchMap(() => this.getAllNotification()))
      .subscribe((data) => {
        if (localStorage.getItem('Currenturl') != '/pos') {
          const filtered = data.filter((item: any) => item.isRead == true && item.moduleName != 'POS');
          const newItems = filtered.filter((item: any) => !this.previousNotificationIds.includes(item.id));
          if (newItems.length > 0) {
            this.audio.src = 'assets/sounds/notification.mp3';
            this.audio.load();
            this.playNotificationSound();
          }
          // Update current data and cache IDs
          this.notificationData = filtered;
          this.previousNotificationIds = filtered.map((item: any) => item.id);

          this.cdr.detectChanges();
        }
      });
  }

  ngOnDestroy(): void {
    // Stop polling and remove the listener when this component goes away
    this.notificationAppCall?.unsubscribe();
    document.removeEventListener('click', this.handleUserInteraction);
  }

  handleUserInteraction = () => {
    this.userInteracted = true;
  }

  getUserDetails() {
    this._http.get(this.apiUrl + GET_USER + this.id).subscribe((response) => {
      if (response) {
        this.userData = response;
        this.profileImage = response.image ? this.imageURL + response.image : 'assets/images/user/user1.png';
      }
    });
  }

  getAllNotification() {
    return this._http.get(this.apiUrl + GET_NOTIFICATION);
  }

  notificationClick(notification: any): void {
    const otherId = this.notificationData
      .filter((item: any) => item.moduleId === notification.moduleId)
      .map((item: any) => item.recordId);

    const notiExtra: NavigationExtras = { state: { record: notification.id, otherId } };
    this.dropdown.close();

    const module = notification.moduleName?.toLowerCase();
    if (module === 'creditrequest') {
      this.route.navigate(['pos-main/credit-request'], notiExtra);
    } else if (module === 'sendrequisition') {
      this.route.navigate(['master/requisition'], notiExtra);
    } else {
      this.route.navigate(['master/received-requisition'], notiExtra);
    }

    this._http.put(this.apiUrl + GET_NOTIFICATION + '/update', {
      id: notification.id, isRead: false
    }).subscribe();
  }

  onChatToggle(friendID: number): void {
    this.friendId = friendID;
    this.chatMessage = !this.chatMessage;
  }

  logout(): void {
    this.notificationAppCall?.unsubscribe();
    this.auth.logout();
    this.route.navigate(['auth/signin']);
  }

  playNotificationSound(): void {
    this.audio.pause();
    this.audio.currentTime = 0;
    if (this.userInteracted) {
      this.audio.play().catch(() => {});
    }
  }
}